import type { AfterViewInit, OnDestroy } from '@angular/core';
import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  Output,
  ViewChild,
  inject,
} from '@angular/core';
import type { FaceFilterThree } from '@freelancer/face-filter';
import { FaceFilterService } from '@freelancer/face-filter';
import { Assets } from '@freelancer/ui/assets';
import { VideoComponent } from '@freelancer/ui/video';
import type {
  Category,
  Classifications,
  FaceLandmarkerResult,
} from '@mediapipe/tasks-vision';
import type {
  Mesh,
  MeshStandardMaterial,
  PerspectiveCamera,
  Scene,
  WebGLRenderer,
} from 'three';
import type { GLTF } from 'three/examples/jsm/loaders/GLTFLoader';

@Component({
  standalone: false,
  selector: 'fl-face-filter-raccoon',
  template: `
    <canvas
      #faceFilterCanvas
      class="Overlay"
      [class.MirrorMode]="mirrorMode"
    ></canvas>
  `,
  changeDetection: ChangeDetectionStrategy.OnPush,
  styleUrls: ['./face-filter-raccoon.component.scss'],
})
export class FaceFilterRaccoonComponent implements AfterViewInit, OnDestroy {
  private assets = inject(Assets);
  private changeDetectorRef = inject(ChangeDetectorRef);
  private faceFilterService = inject(FaceFilterService);

  @Input()
  activeRecordingElementRef: VideoComponent;

  @Input()
  mirrorMode = false;

  @ViewChild('faceFilterCanvas')
  faceFilterCanvasRef: ElementRef<HTMLCanvasElement>;

  @Output()
  faceFilterLoaded = new EventEmitter<void>();

  canvasContext: WebGL2RenderingContext | null;

  faceDetectionLoopId: number;
  renderingLoopId: number;

  isModelLoaded = false;

  private THREE: FaceFilterThree;

  private _threeCamera: PerspectiveCamera;
  private _threeRenderer: WebGLRenderer;
  private _threeScene: Scene;

  private gltf: GLTF;
  private texture: ImageBitmap;

  private morphTargetMesh: Mesh;

  async ngAfterViewInit(): Promise<void> {
    this.faceFilterCanvasRef.nativeElement.width =
      this.activeRecordingElementRef.media.nativeElement.offsetWidth;
    // Allow some space at the bottom for the video controls
    this.faceFilterCanvasRef.nativeElement.height =
      this.activeRecordingElementRef.media.nativeElement.offsetHeight - 30;

    await this.faceFilterService.initiateFaceLandmarker();

    await this.initThreeScene();

    this.trackFace();

    this.render();

    this.faceFilterLoaded.emit();

    this.changeDetectorRef.markForCheck();
  }

  ngOnDestroy(): void {
    if (this.faceDetectionLoopId) {
      cancelAnimationFrame(this.faceDetectionLoopId);
    }

    if (this.renderingLoopId) {
      cancelAnimationFrame(this.renderingLoopId);
    }
  }

  private trackFace(): void {
    const results = this.faceFilterService.detectForVideo(
      this.activeRecordingElementRef.media.nativeElement,
    );

    if (!results) {
      return;
    }

    this.updateFacialTransforms(results);
    this.faceDetectionLoopId = requestAnimationFrame(this.trackFace.bind(this));
  }

  private async initThreeScene(): Promise<void> {
    const three = await this.faceFilterService.loadThree();
    if (!three) {
      return;
    }

    this.THREE = three;

    this.THREE.Cache.enabled = true;

    this._threeScene = new this.THREE.Scene();

    this.canvasContext =
      this.faceFilterCanvasRef.nativeElement.getContext('webgl2');

    if (!this.canvasContext) {
      return;
    }

    this._threeRenderer = new this.THREE.WebGLRenderer({
      antialias: true,
      context: this.canvasContext,
    });

    this._threeRenderer.outputEncoding = this.THREE.sRGBEncoding;
    this._threeRenderer.autoClear = false;

    await this.loadModel();

    const aspectRatio =
      this.faceFilterCanvasRef.nativeElement.width /
      this.faceFilterCanvasRef.nativeElement.height;
    this._threeCamera = new this.THREE.PerspectiveCamera(
      60,
      aspectRatio,
      0.01,
      2000,
    );

    const whiteColor = new this.THREE.Color('white');

    const ambientLight = new this.THREE.AmbientLight(whiteColor, 0.5);
    this._threeScene.add(ambientLight);

    const dirLight = new this.THREE.DirectionalLight(whiteColor, 0.5);
    dirLight.position.set(0, 1, 0);
    this._threeScene.add(dirLight);
  }

  private render(): void {
    this._threeRenderer.resetState();
    this._threeRenderer.render(this._threeScene, this._threeCamera);

    this.renderingLoopId = requestAnimationFrame(this.render.bind(this));
  }

  private async loadModel(): Promise<void> {
    this.isModelLoaded = false;

    const textureLoader = new this.THREE.ImageBitmapLoader();

    this.texture = await textureLoader.loadAsync(
      this.assets.getUrl('face-filter/raccoon_texture.jpg'),
    );

    const loader = new this.THREE.GLTFLoader();
    this.gltf = await loader.loadAsync(
      this.assets.getUrl('face-filter/raccoon_min.glb'),
    );

    this.gltf.scene.traverse(async (obj): Promise<void> => {
      const mesh = obj as Mesh;

      // Skip if not a mesh
      if (!mesh.isMesh) {
        return;
      }

      // Skip if mesh doesn't include morphable targets
      if (!mesh.morphTargetDictionary || !mesh.morphTargetInfluences) {
        return;
      }

      mesh.frustumCulled = false;

      const canvasTexture = new this.THREE.CanvasTexture(this.texture);

      const meshMaterial = mesh.material as MeshStandardMaterial;

      meshMaterial.map = canvasTexture;

      meshMaterial.map.source = new this.THREE.Source(this.texture);
      meshMaterial.map.source.needsUpdate = true;

      meshMaterial.map.encoding = this.THREE.sRGBEncoding;
      meshMaterial.map.userData = {
        mimeType: 'image/jpeg',
      };
      meshMaterial.map.needsUpdate = true;

      meshMaterial.metalness = 0.0;
      meshMaterial.metalnessMap = null;
      meshMaterial.needsUpdate = true;

      this.morphTargetMesh = mesh;
    });

    this._threeScene.add(this.gltf.scene);

    this.isModelLoaded = true;
  }

  private updateFacialTransforms(results: FaceLandmarkerResult): void {
    if (!results || !this.isModelLoaded) {
      return;
    }

    this.updateTranslation(results);
    this.updateBlendShapes(results);
  }

  private updateBlendShapes(results: FaceLandmarkerResult): void {
    // Skip if no blendshapes
    if (!results.faceBlendshapes || !results.faceBlendshapes[0]) {
      return;
    }

    const retargettedCategories = this.retarget(results.faceBlendshapes);

    if (
      !this.morphTargetMesh.morphTargetDictionary ||
      !this.morphTargetMesh.morphTargetInfluences
    ) {
      return;
    }

    // Update morph targets in the raccoon model
    for (const category of retargettedCategories) {
      const idx =
        this.morphTargetMesh.morphTargetDictionary[category.categoryName];
      this.morphTargetMesh.morphTargetInfluences[idx] = category.score;
    }
  }

  private updateTranslation(results: FaceLandmarkerResult): void {
    // Set scale to 0 to hide when no face detected
    const scale = results.facialTransformationMatrixes.length === 0 ? 0 : 40;

    const matrix = new this.THREE.Matrix4();
    const matrixData = results.facialTransformationMatrixes[0]?.data;

    if (matrixData && matrixData.length > 0) {
      matrix.fromArray(matrixData);
    }

    matrix.scale(new this.THREE.Vector3(scale, scale, scale));

    this.gltf.scene.matrixAutoUpdate = false;

    // Set new position and rotation from matrix
    this.gltf.scene.matrix.copy(matrix);
  }

  private retarget(blendshapes: Classifications[]): Category[] {
    const categories = blendshapes[0]?.categories;

    if (!categories) {
      return [];
    }

    const retargettedCategories: Category[] = categories.filter(
      (category: Category) => {
        // Compared to the human face, these face features should be
        // bigger on the raccoon model, hence we're increasing the
        // score by 20% to make the raccoon face more expressive.
        if (
          [
            'browOuterUpLeft',
            'browOuterUpRight',
            'eyeBlinkLeft',
            'eyeBlinkRight',
          ].includes(category.categoryName)
        ) {
          category.score *= 1.2;
        }

        // The face landmarker has this category which is not used in
        // the raccoon model hence we're skipping it.
        return category.categoryName !== '_neutral';
      },
    );

    return retargettedCategories;
  }
}
